import React, { useLayoutEffect, useRef, useState } from 'react';
import { Bx, Typo } from '@curry-group/mui-curcuma';
import { CommunicationInput } from '../../components/communication/input';

export interface IBotChatStartProps {
  fullName?: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  /** Wrapper des Eingabefelds, damit der BotChat beim ersten Absenden die Startposition für den "Morph" messen kann. */
  inputRef?: React.Ref<HTMLDivElement>;
}

/**
 * BMBFMTOL-1530: Eigenständige Startansicht des BotChats (Claude/ChatGPT-Stil), getrennt vom normalen Chat-Frame.
 * Das Eingabefeld steht mittig im Gesprächsbereich, die Begrüßung darunter. Ist die Begrüßung höher als die
 * untere Hälfte, rutscht das Feld so weit nach oben, dass der Text vollständig sichtbar bleibt.
 *
 * Die verfügbare Höhe wird gemessen (Abstand von der Oberkante dieser Ansicht bis zum unteren Fensterrand),
 * weil sie über CSS-%-Höhen im App-Frame nicht bis hierher durchgereicht wird.
 */
export const BotChatStart: React.FC<IBotChatStartProps> = ({ fullName, value, onChange, onSubmit, disabled, inputRef }) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number>();

  useLayoutEffect(() => {
    const measure = () => {
      const el = rootRef.current;
      if (!el) return;
      setHeight(Math.max(0, window.innerHeight - el.getBoundingClientRect().top));
    };
    measure();
    window.addEventListener('resize', measure);
    const header = document.getElementById('frame__header');
    const observer = header && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (header) observer?.observe(header);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, []);

  return (
    <div
      ref={rootRef}
      style={{ display: 'flex', flexDirection: 'column', minHeight: height, boxSizing: 'border-box', padding: '32px 16px', color: '#000' }}
    >
      <div style={{ flex: '1 1 0' }} />
      <Bx width="100%" maxWidth={760} mx="auto">
        <div ref={inputRef}>
          <CommunicationInput
            embedded
            hideSubmitUntilInput
            placeholder="Nachricht an den Bot verfassen"
            canWriteMessage
            allowAttachments={false}
            disabled={disabled}
            value={value}
            onChange={onChange}
            onSubmit={onSubmit}
          />
        </div>
      </Bx>
      <div style={{ flex: '1 1 0', width: '100%', maxWidth: 760, margin: '32px auto 0', padding: '0 24px', boxSizing: 'border-box' }}>
        <Typo variant="h2" component="h1" style={{ color: 'inherit' }}>
          Hallo {fullName || ''},
        </Typo>
        <Bx mt={1}>
          <Typo variant="body1" component="div" style={{ color: 'inherit' }}>
            der Medtec Online-Chatbot hilft Ihnen bei der Recherche nach:
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              <li>fachlichen Inhalten auf Medtec Online</li>
              <li>Expertinnen und Experten aus unserem Autorenpool</li>
              <li>themenrelevanten Communities sowie</li>
              <li>aktuellen, relevanten News</li>
            </ul>
          </Typo>
        </Bx>
        <Bx mt={2}>
          <Typo variant="body1" style={{ color: 'inherit' }}>
            Möchten Sie lediglich einen Überblick über alle Rubriken der Plattform erhalten, fragen Sie explizit nach
            einem Plattformüberblick.
          </Typo>
        </Bx>
      </div>
    </div>
  );
};
